import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

/**
 * Help-article feedback («Var dette nyttig?»). One vote per (article, user); staff mark
 * comments reviewed from the article stats drawer.
 */
class ArticleFeedbackStore extends LocalModel {
    constructor() {
        super('articleFeedback', {
            namePlural: 'articleFeedbacks',
            namePluralReal: 'articlefeedback',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/articlefeedback/',
                    params: {
                        limit: 100,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/articlefeedback/',
                    params: {},
                },
                save: {
                    url: '/api/articlefeedback/',
                    params: {},
                },
                delete: {
                    url: '/api/articlefeedback/',
                    params: {},
                },
            },
        });
    }

    @observable articleFeedback = {};

    @observable articleFeedbacks = [];

    // The logged-in user's own vote on the article currently open in the help center.
    @observable myVote = {};

    // Admin / media worker view: every row for the open article plus the vote totals.
    @observable counts = { up: 0, down: 0 };

    @observable saving = false;

    @action
    clearMine() {
        this.myVote = {};
        this.articleFeedbacks = [];
        this.counts = { up: 0, down: 0 };
    }

    // The GET returns only the caller's row for regular users but EVERY row for admins and
    // media workers, so the caller's vote must be picked by user id, never rows[0].
    async fetchRows(articleId) {
        const response = await util.fetchApi('/api/articlefeedback/', { method: 'GET' }, { article: articleId });
        const rows = response?.status === 200 && Array.isArray(response.data) ? response.data : [];
        const counts = response?.counts || { up: 0, down: 0 };
        return { rows, counts };
    }

    @action
    async loadMine(articleId, userId) {
        if (!(articleId > 0)) {
            this.updateKeyValue('myVote', {});
            return {};
        }
        const { rows } = await this.fetchRows(articleId);
        const mine = rows.find(r => r.user === userId) || {};
        this.updateKeyValue('myVote', mine);
        return mine;
    }

    // Admin / media worker: one request fills the caller's vote, the full row list and the totals.
    @action
    async loadForArticle(articleId, userId) {
        if (!(articleId > 0)) {
            this.clearMine();
            return [];
        }
        const { rows, counts } = await this.fetchRows(articleId);
        this.updateKeyValue('articleFeedbacks', rows);
        this.updateKeyValue('counts', { up: counts.up || 0, down: counts.down || 0 });
        this.updateKeyValue('myVote', rows.find(r => r.user === userId) || {});
        return rows;
    }

    @action
    async markReviewed(id) {
        const response = await this.setStatus(id, 'reviewed');
        if (response?.status === 202) {
            const rows = this.articleFeedbacks.map(r => (r.id === id ? { ...r, status: 'reviewed' } : r));
            this.updateKeyValue('articleFeedbacks', rows);
        }
        return response;
    }

    @action
    async vote({ articleId, vote, comment = '' }) {
        this.updateKeyValue('saving', true);
        const response = await util.fetchApi('/api/articlefeedback/', { method: 'POST' }, {
            article: articleId,
            vote,
            comment,
        });
        if (response?.status === 200 || response?.status === 201) {
            this.updateKeyValue('myVote', response.data || {});
        }
        this.updateKeyValue('saving', false);
        return response;
    }

    async setStatus(id, status) {
        return util.fetchApi(`/api/articlefeedback/${id}`, { method: 'PATCH' }, { status });
    }
}

const store = new ArticleFeedbackStore();
export default store;
